import {
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import {
  Errors,
  Table,
  createDataSource,
  useDatasource,
} from "components/Bable"
import { fetchMarketTargetParams, patchMarketTargetParams } from "api/targets"
import { useMutation, useQuery } from "react-query"

import { COLUMNS } from "./columns"
import { Loading } from "components/Loading"
import { SnackBar } from "components/SnackBar"
import { TargetParams } from "models/targets"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

const getRowId = (x: TargetParams) => x.id

export const globalState = createDataSource({ getRowId })

const TargetParameters = () => {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()

  const [searchValue] = useState<string>("")

  const actionAllowed = hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
  const [error, setError] = useState<Errors>()

  useEffect(() => globalState.setErrors({}), [])

  // ** fetch target-parameter
  const { isLoading } = useQuery(["target-param"], fetchMarketTargetParams, {
    onSuccess: (d: TargetParams[]) => {
      globalState.setData(d)
    },
  })

  const [patch] = useMutation(patchMarketTargetParams, {
    onSuccess: (d: TargetParams) => globalState.updateRow(d),
    onError: (e: Errors, mf: TargetParams) => {
      const error = e as Errors
      !!mf?.id && globalState.setRowErrors(mf?.id, error)
      setError(error)
    },
  })

  const onUpdate = useCallback(
    async (row, label, value) => {
      patch({ id: row.id || "", [label]: value } as TargetParams)
    },
    [patch],
  )
  const handleCloseError = (event?: SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return
    }
    setError(undefined)
  }

  const source = useDatasource({
    state: globalState,
    filters: [
      {
        active: !!searchValue.trim(),
        predicate: (row) => {
          const reg = new RegExp(searchValue, "ig")
          return reg.test(row?.label || "")
        },
      },
    ],
  })
  const context = { onUpdate }

  return (
    <contextOptions.Provider value={context}>
      <div className={styles.tittle}>
        <strong>{t("admin.targetParameters.tittle")}</strong>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Table<TargetParams>
            columns={COLUMNS(actionAllowed)}
            source={source}
            height={200}
            className={styles.table}
          />
          <SnackBar
            open={!!error}
            severity="error"
            onClose={handleCloseError}
            message={error?.message || ""}
          />
        </>
      )}
    </contextOptions.Provider>
  )
}
export default TargetParameters

const contextOptions = createContext<any | undefined>(undefined)

export const useContextOptions = () => {
  const options = useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
      &[data-column-id="variables"],
      &[data-column-id="semantic"] {
        display: flex;
        flex: 1;
        justify-content: flex-start;
        padding-left: var(--spacing-s);
      }
      display: flex;
      flex: 1;
      justify-content: flex-start;
      padding-left: var(--spacing-xs);
    }

    .BodyBody-2BlackRegular {
      margin: 0;
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  section: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 10px;
    margin-bottom: 65px;
    > * {
      margin-bottom: 23px;
    }
  `,
  tittle: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 32px;
    margin-bottom: 16px;
    > * {
      margin-bottom: 1px;
    }
  `,
  label: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 20px;
    > * {
      margin-bottom: 3px;
    }
  `,
}
